<script lang="ts">
  import type { DebtorData, SwedishId } from "../../../../apis/parkito2";
  import type { DebtorData as OldDebtorData } from "../../../../apis/parkito";
  import { DebtorLanguage } from "$apis/parkito2";
  import CustomButton from "../../../../shared/components/CustomButton.svelte";
  import { Header, Toast } from "$components";
  import { Col2Grid, Ver } from "$components";
  import {
    Form,
    FormInput,
    Validate,
  } from "../../../../shared/lib/validation_next";
  import ValInputField from "../../../../shared/components/ValInputField.svelte";
  import ValCheckbox from "../../../../shared/components/ValCheckbox.svelte";
  import { isPersonVariant } from "../../../../shared/utils/validation";
  import ValSelect from "../../../../shared/components/ValSelect.svelte";
  import Separator from "../../../../shared/components/Separator.svelte";

  type Props = {
    debtor: FormInput<DebtorData | OldDebtorData>;
    buttonInvisible?: boolean;
    submitForm?: (params: DebtorData | OldDebtorData) => void;
  };
  const {
    debtor,
    buttonInvisible = false,
    submitForm,
  }: Props = $props();

  let initialValues = $debtor.res.optional();

  /**
   * Form fields
   */
  let fieldName = Form.field(
    Validate.required("Namn måste anges").and(Validate.len(2, 128)),
    initialValues?.name ?? "",
  );
  let fieldAdress = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.address ?? "",
  );
  let fieldAddressCo = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.address ?? "",
  );
  let fieldZip = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.zip ?? "",
  );
  let fieldApartmentNr = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.apartmentNr ?? "",
  );
  let fieldCity = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.city ?? "",
  );
  let fieldPhone = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.phone ?? "",
  );
  let fieldEmailContact = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.contactEmail ?? "",
  );
  let fieldProtectedIdentity = Form.checkbox(
    Validate.boolean(),
    initialValues?.protectedIdentity ?? false,
  );
  let fieldProtectedMailDelivery = Form.checkbox(
    Validate.boolean(),
    initialValues?.protectedMailDelivery ?? false,
  );

  let fieldYourReference = Form.field(
    Validate.optional(Validate.len(0, 64)),
    initialValues?.yourReference ?? "",
  );

  export const updateDebtorData = (updatedValues?: DebtorData) => {
    // Because Form.field stores the debtor values
    // in writable stores we gotta listen to the changes manually
    fieldAdress.input.set(updatedValues?.address ?? "");
    fieldAddressCo.input.set(updatedValues?.addressCo ?? "");
    fieldApartmentNr.input.set(updatedValues?.apartmentNr ?? "");
    fieldCity.input.set(updatedValues?.city ?? "");
    fieldName.input.set(updatedValues?.name ?? "");
    fieldPhone.input.set(updatedValues?.phone ?? "");
    fieldProtectedIdentity.input.set(
      updatedValues?.protectedIdentity ?? false,
    );
    fieldProtectedMailDelivery.input.set(
      updatedValues?.protectedMailDelivery ?? false,
    );
    fieldYourReference.input.set(updatedValues?.yourReference ?? "");
    fieldZip.input.set(updatedValues?.zip ?? "");
    fieldEmailContact.input.set(updatedValues?.contactEmail ?? "");
  };

  // Swedish ID - organisation / person fields
  const sidChoices = [
    { label: "Person", value: "person", disabled: false },
    { label: "Organisation", value: "organisation", disabled: false },
  ];
  const sidSelect = Form.multiSelect(
    sidChoices,
    sidChoices[
      initialValues?.sid ? isPersonVariant(initialValues.sid) ? 0 : 1 : 0
    ],
  );
  const sidFields = {
    person: Form.field(
      Validate.required("Personnummer måste anges")
        .and(Validate.validatePersonnummer())
        .map((s) => ({ person: s })),
      initialValues?.sid && isPersonVariant(initialValues?.sid)
        ? initialValues.sid.person
        : "",
    ),
    organisation: Form.field(
      Validate.required("Orgnummer måste anges")
        .and(Validate.validateOrganisationsnummer())
        .map((s) => ({ organisation: s })),
      initialValues?.sid && !isPersonVariant(initialValues.sid)
        ? initialValues.sid.organisation
        : "",
    ),
  };
  const sid = Form.oneOf<SwedishId>(sidSelect, sidFields);

  // Form object
  let form = Form.object<DebtorData | OldDebtorData>({
    addressCo: fieldAddressCo,
    address: fieldAdress,
    apartmentNr: fieldApartmentNr,
    city: fieldCity,
    contactEmail: fieldEmailContact,
    language: Form.fixed<DebtorLanguage>(DebtorLanguage.SWEDISH),
    phone: fieldPhone,
    name: fieldName,
    zip: fieldZip,
    protectedIdentity: fieldProtectedIdentity,
    protectedMailDelivery: fieldProtectedMailDelivery,
    sid: Form.nest(sid),
    yourReference: fieldYourReference,
  });
  debtor.subscribeTo(form);

  const requestFormSubmit = async () => {
    if (!$form.res.isOk()) {
      Toast.error("Formuläret innehåller fel");
      return;
    }
    const formValue = $form.res.value;

    // Build the DebtorData object
    const params: DebtorData = {
      name: formValue.name,
      address: formValue.address,
      zip: formValue.zip,
      city: formValue.city,
      phone: formValue.phone,
      contactEmail: formValue.contactEmail,
      protectedIdentity: formValue.protectedIdentity,
      protectedMailDelivery: formValue.protectedMailDelivery,
      apartmentNr: formValue.apartmentNr,
      sid: formValue.sid,
      yourReference: formValue.yourReference,
    };

    if (submitForm) submitForm(params);
  };
</script>

<Ver gap="m">
  <Col2Grid>
    <ValSelect
      label="Kontotyp"
      name="sid-kind"
      field={sidSelect}
    />
    {#if $sidSelect.res.isOk() && $sidSelect.res.value === "person"}
      <ValInputField
        field={sidFields.person}
        label="Personnummer*"
        placeholder="Person- eller samordningsnr"
      />
    {/if}
    {#if       $sidSelect.res.isOk() && $sidSelect.res.value === "organisation"}
      <ValInputField
        field={sidFields.organisation}
        label="Orgnummer*"
        placeholder="XXXXXX-XXXX"
      />
    {/if}
  </Col2Grid>
  <Separator />

  <Header size="3">Uppgifter</Header>
  <Col2Grid>
    <ValInputField
      reserveErrorMsgHeight
      name="name"
      field={fieldName}
      label="Namn*"
      placeholder="Namn"
    />

    <!--ValInputField
      reserveErrorMsgHeight
      name="your-reference"
      field={fieldYourReference}
      label="Kundens referens"
      placeholder="Kundens referens"
    /-->
    <ValInputField
      reserveErrorMsgHeight
      name="address"
      field={fieldAdress}
      label="Adress"
      placeholder="Adress"
    />
    <ValInputField
      reserveErrorMsgHeight
      name="apartment-nr"
      field={fieldApartmentNr}
      label="Lägenhetsnummer"
      placeholder="Lägenhetsnummer"
    />

    <ValInputField
      reserveErrorMsgHeight
      name="zip"
      field={fieldZip}
      label="Postnummer"
      placeholder="Postnummer"
    />
    <ValInputField
      reserveErrorMsgHeight
      name="city"
      field={fieldCity}
      label="Stad"
      placeholder="Stad"
    />
    <ValInputField
      reserveErrorMsgHeight
      name="phone"
      field={fieldPhone}
      label="Telefon"
      placeholder="Telefon"
    />
    <ValInputField
      reserveErrorMsgHeight
      name="email-contact"
      field={fieldEmailContact}
      label="E-post - kontakt"
      placeholder="Epost - kontakt"
    />
  </Col2Grid>

  <Col2Grid>
    <ValCheckbox
      name="protected-identity"
      field={fieldProtectedIdentity}
      label="Skyddad identitet"
    />
    <ValCheckbox
      name="protected-mail-delivery"
      field={fieldProtectedMailDelivery}
      label="Skyddad postutdelning"
    />
  </Col2Grid>

  {#if !buttonInvisible}
    <div class="flex pt-[24px] justify-end">
      <CustomButton
        disabled={!($form.res.isOk() && $form.hasChanged)}
        type="primary"
        onClick={requestFormSubmit}
      >Uppdatera</CustomButton>
    </div>
  {/if}
</Ver>
